/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */

import { Platform } from 'react-native'

import Client from '../../internal/net/Client'
import Store from '../../internal/repo/Store'

export default class WebSockets {
  store: Store
  client: Client
  address: string
  socket: WebSocket | null
  delayTime: number
  shouldRetry: boolean
  retryTimer: any

  constructor(store: Store, client: Client, address: string) {
    this.store = store
    this.client = client
    this.address = address

    /** The WebSocket connection */
    this.socket = null

    /** Time until the next retry */
    this.delayTime = 1000

    /** If true, the websocket will continue to retry the connection if it fails */
    this.shouldRetry = false
  }

  /** This will be true if the connection is ready to send and receive messages */
  get isOpen() {
    return this.socket && this.socket.readyState === 1
  }

  /**
   * The connect function establishes a connection to the WebSocket.
   * @public
   * @return {Promise<WebSockets>}
   */
  async connect() {
    // Stay connected after this point
    this.shouldRetry = true

    // if the websocket is connected or connecting already, then stop
    if (this.socket) {
      return this
    }

    // before we connect, make sure the token is valid, or else retry again soon
    try {
      await this.client.checkToken()
    } catch (err) {
      console.warn('WebSocket unable to get client token! Will retry soon...')
      this.retryConnection()
      return this
    }

    // if the websocket is connected or connecting already, then stop
    if (this.socket) {
      return this
    }

    if (!this.store.appID || !this.store.token) {
      throw new Error(`No AppID (${this.store.appID}) or Token (${this.store.token}) in Store`)
    }

    // Create the websocket
    const url = `${this.address}/ws?app_id=${encodeURIComponent(
      this.store.appID
    )}&token=${encodeURIComponent(this.store.token)}`
    const extra =
      Platform.OS === 'ios'
        ? [
            undefined,
            {
              headers: {
                'Accept-Language': 'en,en-US;q=0.9,ru;q=0.8,de;q=0.7',
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
                'User-Agent':
                  'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_13_5) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/67.0.3396.87 Safari/537.36'
              }
            }
          ]
        : []

    this.socket = new WebSocket(
      url,
      // @ts-expect-error https://stackoverflow.com/questions/50937427/why-does-websocket-close-immediately-after-open-in-react-native-with-code-1000
      ...extra
    )
    this.socket.addEventListener('open', this.handleConnected.bind(this))
    this.socket.addEventListener('message', this.handleMessage.bind(this))
    this.socket.addEventListener('error', this.handleError.bind(this))
    this.socket.addEventListener('close', this.handleClose.bind(this))
    // Done
    return this
  }

  /**
   * This sends a message through the web socket
   * @param {*} cmd
   */
  sendMessage(cmd: any) {
    if (this.socket && this.socket.readyState === 1) {
      this.socket.send(JSON.stringify(cmd))
    } else {
      console.warn('WebSocket: Attempted to send message up, but the socket is not ready.')
    }
  }

  /**
   * The handleMessage function allows the different types of messages to be returned:
   * stateUpdate, inventory, activity, and, info.
   * @private
   * @param  {JSON<Object>} e A JSON Object that is passed into the function from connect()
   * @return {JSON<Object>}  A JSON Object is returned containing the list of chosen message types
   */
  handleMessage(e: any) {
    try {
      // Handle both vatomNet and vatominc web sockets
      const ed = e.data ? JSON.parse(e.data) : e
      this.trigger('message', ed)
    } catch (err) {
      console.warn('WebSocket unable to get client token! Will try to reconnect...')
      this.client.checkToken().then(() => {
        this.retryConnection()
      })
    }
  }

  trigger(arg0: string, ed: any) {
    throw new Error('Method not implemented.')
  }

  /**
   * Lets the User know that the connection is connected
   * @private
   * @param  {Event<SocketStatus>} e no need for inputting the parameter
   * @return {Function<connected>} triggers the connected function
   */
  handleConnected(e: any) {
    this.delayTime = 1000
    this.trigger('connected', e)
  }

  /**
   * When the connection drops or the Websocket is closed.
   * This function will auto-retry connection until successfully connected
   * @private
   * @return {Promise<WebSockets>} returns the connection function
   */
  retryConnection() {
    // Clear previous retry timer
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
    }

    // Create a new retry timer
    this.retryTimer = setTimeout(() => {
      // Clear timer
      this.retryTimer = null

      // Check if we want to be connected
      if (!this.shouldRetry) {
        return
      }

      // Increase retry delay for next time
      if (this.delayTime < 4000) {
        this.delayTime += 1000
      }

      // connect again
      this.connect()
    }, this.delayTime)
  }

  /**
   * Handles the Web socket error event. We don't need to retry, because handleClose is also called on errors.
   * @private
   * @param {Error} err The error that happened
   */
  handleError(err: any) {
    this.socket = null

    console.error(`[WebSocket] Connection failed: ${JSON.stringify(err)}`)
  }

  /**
   * Handles the Web socket close event
   * @private
   * @param  {Event} e no need for inputting, It is a Websocket Event
   */
  handleClose() {
    console.info('[WebSocket] Connection closed. Retrying')

    this.socket = null
    this.retryConnection()
  }

  /**
   * @public
   * Forcefully closes the Web socket.
     Note: Socket will be set to null. Auto connect will be disabled.
   */
  close() {
    // Prevent retrying
    this.shouldRetry = false

    // Cancel retry timer if there is one
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }

    // Close socket if it's open
    if (this.socket) {
      this.socket.close()
      this.socket = null
    }
  }
}
